import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { TunerOverlay } from "../TunerOverlay";
import { LogoutButton } from "../LogoutButton";
import { useInstrument } from "../../contexts/InstrumentContext";
import { INSTRUMENT_OPTIONS } from "../../lib/instrumentEngine";
import { HOME_THEME as T } from "../../lib/homeTheme";

export interface BordonaAppBarProps {
  /** Sandbox inmersivo: enlace de vuelta al hub Explorar */
  exploreBack?: boolean;
}

/**
 * App bar Serenity compartida: Home, Explorar (hub) y sandbox inmersivo.
 */
export const BordonaAppBar: React.FC<BordonaAppBarProps> = ({ exploreBack = false }) => {
  const navigate = useNavigate();
  const { instrument, setInstrument, requestInstrumentPick } = useInstrument();
  const [tunerOpen, setTunerOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 flex items-start justify-between px-6 py-6 md:px-10 md:py-8 pointer-events-none font-sans">
        <div className="pointer-events-auto flex items-center min-h-[1.25rem]">
          {exploreBack ? (
            <button
              type="button"
              onClick={() => navigate("/explore")}
              className="inline-flex items-center gap-2 text-[11px] font-sans tracking-[0.14em] uppercase transition-colors duration-300 hover:text-[#c4b5fd]"
              style={{ color: T.muted }}
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
              Explorar
            </button>
          ) : (
            <button
              type="button"
              onClick={() => navigate("/home")}
              className="text-[11px] font-serif font-light italic tracking-[0.12em] uppercase transition-colors duration-300 hover:text-[#c4b5fd]"
              style={{ color: T.text }}
              title="Inicio"
            >
              Bordona
            </button>
          )}
        </div>

        <div className="pointer-events-auto flex items-center gap-5 md:gap-6">
          <select
            value={instrument}
            onChange={(e) => setInstrument(e.target.value as typeof instrument)}
            className="appearance-none bg-transparent text-[11px] font-sans tracking-[0.14em] uppercase cursor-pointer focus:outline-none"
            style={{ color: T.muted }}
            aria-label="Instrumento"
          >
            {INSTRUMENT_OPTIONS.map((inst) => (
              <option key={inst.id} value={inst.id} style={{ background: T.bg, color: T.text }}>
                {inst.label}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={() => navigate("/clases")}
            className="text-[11px] font-sans tracking-[0.14em] uppercase transition-colors duration-300 hover:text-[#c4b5fd]"
            style={{ color: T.muted }}
          >
            Clases
          </button>

          <button
            type="button"
            onClick={() => setTunerOpen((v) => !v)}
            className="text-[11px] font-sans tracking-[0.14em] uppercase transition-colors duration-300 hover:text-[#99f6e4] hidden sm:inline"
            style={{ color: T.muted }}
          >
            Afinador
          </button>

          <div className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="text-[11px] font-sans tracking-[0.14em] uppercase transition-colors duration-300 hover:text-[#c4b5fd]"
              style={{ color: T.text }}
            >
              Perfil
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} aria-hidden />
                <div
                  className="absolute right-0 top-full z-50 mt-3 min-w-[10rem] py-2"
                  style={{ background: T.bg, borderTop: `1px solid ${T.lineItem}` }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      requestInstrumentPick();
                      navigate("/instrument");
                      setMenuOpen(false);
                    }}
                    className="block w-full px-4 py-2 text-left text-[11px] font-sans tracking-[0.14em] uppercase transition-colors hover:bg-white/[0.03] hover:text-[#c4b5fd]"
                    style={{ color: T.muted }}
                  >
                    Cambiar instrumento
                  </button>
                  <div className="px-4 py-2">
                    <LogoutButton variant="serenity" tone="dark" />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <TunerOverlay expanded={tunerOpen} onClose={() => setTunerOpen(false)} />
    </>
  );
};

export default BordonaAppBar;
